import React, { useEffect, useRef, useState } from 'react';
import { Notification } from './types';
import styles from './Notifications.module.scss';
import { cls } from '../View/utils';
import InfoIcon from '../common/assets/info.svg';
import ErrorIcon from '../common/assets/alert-triangle.svg';
import SuccessIcon from '../common/assets/checked.svg';
import { NotificationType, notifications } from './types';

const SLIDE_IN_DURATION_MS = 450; // transition.get-duration('m') (transition.scss)
const DEFAULT_HOLD_DURATION_MS = 2500; // $notification-hold-duration (Notifications.module.scss)
const SLIDE_OUT_DURATION_MS = 150; // transition.get-duration('xs') (transition.scss)
const REMOVE_OFFSET_MS = 100;
const ANIMATION_DURATION_MS = SLIDE_IN_DURATION_MS + SLIDE_OUT_DURATION_MS;

interface ItemProps {
  notification: Notification;
  hide: () => void;
  messageType?: NotificationType;
  index?: number;
}

type IconProps = {
  messageType?: NotificationType;
};

const getIcon = ({ messageType = notifications.INFO }: IconProps) => {
  switch (messageType) {
    case notifications.INFO:
      return InfoIcon;
    case notifications.SUCCESS:
      return SuccessIcon;
    case notifications.ERROR:
      return ErrorIcon;
    default:
      return InfoIcon;
  }
};

export const NotificationItem = ({
  notification,
  hide,
  messageType = notifications.INFO,
  index = 0,
}: ItemProps) => {
  const {
    hideAutomatically = true,
    message,
    time: holdDurationMs = DEFAULT_HOLD_DURATION_MS,
  } = notification;
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const hideTimerRef = useRef<number>();
  const removeTimerRef = useRef<number>();
  const hideDelayMs = holdDurationMs + SLIDE_IN_DURATION_MS;
  const removeDelayMS =
    holdDurationMs + ANIMATION_DURATION_MS + REMOVE_OFFSET_MS;

  const hideNotification = () => {
    setIsHidden(true);
    removeTimerRef.current = window.setTimeout(hide, removeDelayMS);
  };

  useEffect(() => {
    if (hideAutomatically) {
      hideTimerRef.current = window.setTimeout(hideNotification, hideDelayMs);
    }
    return () => {
      window.clearTimeout(hideTimerRef.current);
      window.clearTimeout(removeTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = getIcon({ messageType });

  return (
    <div
      className={cls({
        [styles.notificationItem]: true,
        [styles.hidden]: isHidden,
      })}
      aria-live="polite"
      onMouseDown={e => e.preventDefault()}
      tabIndex={0}
      style={
        {
          '--notification-offset': index,
        } as React.CSSProperties
      }
    >
      <div
        className={cls({
          [styles.iconWrapper]: true,
          [styles.iconInfo]: messageType === notifications.INFO,
          [styles.iconSuccess]: messageType === notifications.SUCCESS,
          [styles.iconError]: messageType === notifications.ERROR,
        })}
      >
        <Icon />
      </div>
      <div className={styles.message}>{message}</div>
    </div>
  );
};
